import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Currency } from '@/lib/i18n';

export interface ExchangeRates {
  base_currency: string;
  rates: Record<string, number>;
  rate_date: string;
}

const FALLBACK_RATES: ExchangeRates = {
  base_currency: 'EUR',
  rate_date: new Date().toISOString().slice(0, 10),
  rates: {
    EUR: 1, USD: 1.08, GBP: 0.85, CAD: 1.47, AUD: 1.65, CHF: 0.95,
    SEK: 11.30, NOK: 11.80, JPY: 162, KRW: 1480, PLN: 4.30, BRL: 6.50, TRY: 42,
  },
};

const STORAGE_KEY = 'inkvaluator_exchange_rates_cache';

export function useExchangeRates() {
  return useQuery<ExchangeRates>({
    queryKey: ['exchange-rates'],
    staleTime: 1000 * 60 * 60 * 12, // 12h
    gcTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('exchange_rates')
          .select('base_currency, rates, rate_date')
          .eq('base_currency', 'EUR')
          .maybeSingle();
        if (error) throw error;
        if (data) {
          const result = {
            base_currency: data.base_currency,
            rates: data.rates as Record<string, number>,
            rate_date: data.rate_date,
          };
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(result)); } catch {}
          return result;
        }
      } catch (err) {
        console.warn('Exchange rates fetch failed, using cache/fallback', err);
      }
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) return JSON.parse(cached);
      } catch {}
      return FALLBACK_RATES;
    },
  });
}

/** Convert an amount from a source currency to a target currency using EUR-based rates. */
export function convertAmount(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency,
  rates: Record<string, number>,
): number {
  if (fromCurrency === toCurrency) return amount;
  const rateFrom = rates[fromCurrency] ?? 1;
  const rateTo = rates[toCurrency] ?? 1;
  // Rates are EUR -> X. So amount (in fromCurrency) -> EUR -> toCurrency
  const inEur = amount / rateFrom;
  return inEur * rateTo;
}
